import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import moment from 'moment-mini'
import { combineLatest, Observable } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { productActions } from 'src/app/features/product/product.actions'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { markFormGroupDirty } from 'src/app/shared/utils/mark-form-dirty'
import { SaleItem } from '../sale-item'
import { SaleItemCreateFormState } from '../create-form-state.model'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { rentalActions } from 'src/app/features/rental/rental.actions'
import { rentalSelectors } from 'src/app/features/rental/rental.selectors'
import { saleItemSelectors } from '../sale-item.selectors'

@UntilDestroy()
@Component({
  selector: 'rental-sale-item-form-create',
  templateUrl: 'form.component.html'
})
export class RentalSaleItemFormCreateComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<Partial<SaleItem>>()
  @Output() formStateChange = new EventEmitter<SaleItemCreateFormState>()
  @Input() customerCategoryId: number | string | null
  @Input() loading: boolean = false

  public form: UntypedFormGroup
  public rentalProducts$: Observable<Product[]>
  public selectedProduct: Product
  public dateError: boolean = false
  public productIsRentedOut: boolean = false
  public nextAvailableRentalDate: Date

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<AppState>
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      productId: [null, [Validators.required]],
      quantity: [1, [Validators.required, Validators.min(1)]],
      rentStartDate: [null, [Validators.required]],
      rentEndDate: [null, [Validators.required]]
    })

    this.rentalProducts$ = this.store
      .select(productSelectors.selectAll)
      .pipe(
        map((products) =>
          products
            .filter((product) => {
              const now = moment()

              return (
                product.isRental &&
                product.isActive &&
                product.availableFromDate &&
                moment(product.availableFromDate).isSameOrBefore(now) &&
                (!product.availableTillDate ||
                  moment(product.availableTillDate).isSameOrAfter(now))
              )
            })
            .sort((a, b) => a.description.localeCompare(b.description))
        )
      )

    this.fetchRentalProducts()

    this.form.valueChanges
      .pipe(startWith(this.form.value), untilDestroyed(this))
      .subscribe(() => {
        this.dateError = !this.hasValidRentalPeriod()
        this.emitFormState()
      })

    this.form.valueChanges
      .pipe(
        startWith(this.form.value),
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        untilDestroyed(this)
      )
      .subscribe((value) => this.fetchOverlappingRentals(value))

    combineLatest([
      this.form.valueChanges.pipe(startWith(this.form.value)),
      this.store.select(rentalSelectors.selectAll),
      this.store.select(saleItemSelectors.selectAll)
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([valueChanges, rentals, allSaleItems]) => {
        this.productIsRentedOut = false
        this.nextAvailableRentalDate = null

        if (
          !this.selectedProduct ||
          !valueChanges.productId ||
          !valueChanges.rentStartDate ||
          !valueChanges.rentEndDate
        ) {
          this.emitFormState()
          return
        }

        const overlappingRentals = rentals
          .filter((rental) => rental.productId === valueChanges.productId)
          .filter((rental) => {
            const rentalStartDate = moment(rental.rentStartDate)
            const rentalEndDate = moment(rental.rentEndDate)

            return (
              rentalStartDate.isSameOrBefore(valueChanges.rentEndDate) &&
              rentalEndDate.isSameOrAfter(valueChanges.rentStartDate)
            )
          })

        const productRentalSaleItems = allSaleItems.filter((saleItem) =>
          overlappingRentals.some((rental) => rental.saleItemId === saleItem.id)
        )

        const totalProductRentals = productRentalSaleItems.reduce(
          (sum, item) => sum + Number(item.quantity ?? 0),
          0
        )

        if (
          this.selectedProduct.rentalMaxConcurrent &&
          totalProductRentals >= this.selectedProduct.rentalMaxConcurrent
        ) {
          this.productIsRentedOut = true

          const firstAvailableRentalDate = overlappingRentals
            .filter((overlappingRental) => overlappingRental.isApproved)
            .sort((a, b) =>
              moment(a.rentEndDate).isBefore(moment(b.rentEndDate)) ? -1 : 1
            )?.[0]?.rentEndDate

          if (firstAvailableRentalDate) {
            this.nextAvailableRentalDate = moment(firstAvailableRentalDate)
              .clone()
              .add(this.selectedProduct.rentalDismantlingMinutes + 1, 'minute')
              .toDate()
          }
        }

        this.emitFormState()
      })
  }

  public submitFromParent() {
    markFormGroupDirty(this.form)
    this.dateError = !this.hasValidRentalPeriod()
    this.emitFormState()

    if (!this.form.valid || this.dateError || this.productIsRentedOut) {
      return
    }

    const { productId, quantity, rentStartDate, rentEndDate } = this.form.value

    this.onSubmit.emit({
      productId,
      quantity,
      customerCategory: Number(this.customerCategoryId ?? 0),
      bookingId: null,
      subscriptionId: null,
      courseId: null,
      typeProduct: 'rentalModule.rental',
      params: {
        rentStartDate: moment(rentStartDate),
        rentEndDate: moment(rentEndDate).clone().subtract(1, 'minute')
      }
    })
  }

  public onSearchProducts(term: string) {
    this.fetchRentalProducts(term)
  }

  public productSelected(product: Product) {
    this.selectedProduct = product
    this.setQuantityLimit()
    this.dateError = !this.hasValidRentalPeriod()
    this.emitFormState()
  }

  public getCurrentDate(): Date {
    return new Date()
  }

  private fetchRentalProducts(term?: string) {
    const now = moment().toISOString()

    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            $and: [
              ...(term
                ? [
                    {
                      description: { [CondOperator.CONTAINS_LOW]: term }
                    }
                  ]
                : []),
              { isRental: 1 },
              { isActive: true },
              {
                availableFromDate: {
                  [CondOperator.LOWER_THAN_EQUALS]: now
                }
              },
              {
                $or: [
                  {
                    availableTillDate: {
                      [CondOperator.GREATER_THAN_EQUALS]: now
                    }
                  },
                  { availableTillDate: null }
                ]
              }
            ]
          },
          sort: { field: 'description', order: 'ASC' }
        })
      })
    )
  }

  private setQuantityLimit() {
    const quantityControl = this.form.get('quantity')
    const rentalMaxConcurrent = this.selectedProduct?.rentalMaxConcurrent

    quantityControl.setValidators([
      Validators.required,
      Validators.min(1),
      ...(rentalMaxConcurrent ? [Validators.max(rentalMaxConcurrent)] : [])
    ])

    if (
      rentalMaxConcurrent &&
      Number(quantityControl.value) > rentalMaxConcurrent
    ) {
      quantityControl.setValue(rentalMaxConcurrent)
    }

    quantityControl.updateValueAndValidity()
  }

  private fetchOverlappingRentals(value: {
    productId?: number
    rentStartDate?: moment.Moment
    rentEndDate?: moment.Moment
  }) {
    if (
      !value.productId ||
      !value.rentStartDate ||
      !value.rentEndDate ||
      !this.hasValidRentalPeriod()
    ) {
      return
    }

    this.store.dispatch(
      rentalActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            productId: value.productId,
            rentStartDate: {
              [CondOperator.LOWER_THAN_EQUALS]: moment(
                value.rentEndDate
              ).toISOString()
            },
            rentEndDate: {
              [CondOperator.GREATER_THAN_EQUALS]: moment(
                value.rentStartDate
              ).toISOString()
            }
          },
          join: [{ field: 'saleItem' }]
        })
      })
    )
  }

  private hasValidRentalPeriod(): boolean {
    const rentStartDate = this.form?.value?.rentStartDate
    const rentEndDate = this.form?.value?.rentEndDate

    if (!rentStartDate || !rentEndDate) {
      return true
    }

    const start = moment(rentStartDate)
    const end = moment(rentEndDate)

    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
      return false
    }

    if (
      this.selectedProduct?.rentalMaxMinutes &&
      end.diff(start, 'minutes') > this.selectedProduct.rentalMaxMinutes
    ) {
      return false
    }

    return true
  }

  private emitFormState() {
    this.formStateChange.emit({
      canSubmit: Boolean(
        this.form?.valid && !this.dateError && !this.productIsRentedOut
      ),
      isLoading: Boolean(this.loading),
      submitLabelKey: 'general.save'
    })
  }
}
